import { API_BASE } from "../utils/api";
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/Toast";

const STATUS_CONFIG = {
  submitted:  { label: "Submitted",  bg: "bg-blue-50   dark:bg-blue-900/20",    text: "text-blue-700   dark:text-blue-400",   border: "border-blue-200   dark:border-blue-500/30",   dot: "bg-blue-500" },
  processing: { label: "Processing", bg: "bg-amber-50  dark:bg-amber-900/20",   text: "text-amber-700  dark:text-amber-400",  border: "border-amber-200  dark:border-amber-500/30",  dot: "bg-amber-400 animate-pulse" },
  assessed:   { label: "Assessed",   bg: "bg-emerald-50 dark:bg-emerald-900/20",text: "text-emerald-700 dark:text-emerald-400",border:"border-emerald-200 dark:border-emerald-500/30",dot: "bg-emerald-500" },
  rejected:   { label: "Rejected",   bg: "bg-red-50    dark:bg-red-900/20",     text: "text-red-700    dark:text-red-400",    border: "border-red-200    dark:border-red-500/30",    dot: "bg-red-500" },
};

const OUTCOME_CONFIG = {
  refund:  { label: "Refund Due",     color: "text-luxury-cta",                    icon: "↑" },
  payment: { label: "Tax Payable",    color: "text-red-600 dark:text-red-400",     icon: "↓" },
  nil:     { label: "Nil Assessment", color: "text-luxury-secondary",              icon: "=" },
};

const TYPE_INFO = {
  ITR12:  "Annual income tax return for individuals",
  IRP6:   "Provisional tax return",
  VAT201: "VAT return",
  EMP201: "Employer PAYE return",
};

const FILTERS  = ["All", "ITR12", "IRP6", "Assessed", "Submitted"];
const TAX_YEARS = ["2025", "2024", "2023", "2022", "2021"];

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" });
}

function NewSubmissionModal({ onClose, onAdd, token }) {
  const [form, setForm]       = useState({ type: "ITR12", tax_year: "2025", ref_number: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v)          => setForm(p => ({ ...p, [k]: v }));

  const submit = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/sars/`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        const data = await res.json();
        onAdd(data);
        onClose();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-2xl bg-luxury-surface border border-luxury-border shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-luxury-border bg-luxury-elevated">
          <div>
            <h3 className="font-bold text-luxury-text text-sm">Log New Submission</h3>
            <p className="text-xs text-luxury-muted mt-0.5">Record a SARS submission — saved to your account</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-luxury-muted hover:text-luxury-text hover:bg-luxury-bg transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-luxury-secondary uppercase tracking-wide mb-1.5 block">Return Type</label>
              <select value={form.type} onChange={e => set("type", e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-luxury-border bg-luxury-elevated text-luxury-text text-sm focus:outline-none focus:ring-2 focus:ring-luxury-gold/30">
                {Object.keys(TYPE_INFO).map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-luxury-secondary uppercase tracking-wide mb-1.5 block">Tax Year</label>
              <select value={form.tax_year} onChange={e => set("tax_year", e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-luxury-border bg-luxury-elevated text-luxury-text text-sm focus:outline-none focus:ring-2 focus:ring-luxury-gold/30">
                {TAX_YEARS.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-luxury-secondary uppercase tracking-wide mb-1.5 block">SARS Reference Number</label>
            <input value={form.ref_number} onChange={e => set("ref_number", e.target.value)}
              placeholder="e.g. 9876543210"
              className="w-full px-3 py-2 rounded-xl border border-luxury-border bg-luxury-elevated text-luxury-text text-sm focus:outline-none focus:ring-2 focus:ring-luxury-gold/30 placeholder-luxury-muted" />
          </div>

          <div>
            <label className="text-xs font-semibold text-luxury-secondary uppercase tracking-wide mb-1.5 block">Notes (optional)</label>
            <textarea value={form.notes} onChange={e => set("notes", e.target.value)} rows={2}
              placeholder="Any additional notes..."
              className="w-full px-3 py-2 rounded-xl border border-luxury-border bg-luxury-elevated text-luxury-text text-sm focus:outline-none focus:ring-2 focus:ring-luxury-gold/30 placeholder-luxury-muted resize-none" />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-luxury-border bg-luxury-elevated flex gap-3">
          <button onClick={submit} disabled={loading}
            className="flex-1 py-2.5 rounded-xl bg-luxury-cta hover:bg-luxury-cta-hover disabled:opacity-50 text-white text-sm font-semibold transition-colors flex items-center justify-center gap-2">
            {loading ? (
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : "Log Submission"}
          </button>
          <button onClick={onClose} className="px-5 py-2.5 rounded-xl bg-luxury-bg border border-luxury-border text-luxury-secondary text-sm font-semibold hover:bg-luxury-elevated transition-colors">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function UpdateModal({ sub, onClose, onUpdate, token }) {
  const [status,  setStatus]  = useState(sub.status || "submitted");
  const [outcome, setOutcome] = useState(sub.outcome || "");
  const [amount,  setAmount]  = useState(sub.amount || "");
  const [notes,   setNotes]   = useState(sub.notes || "");
  const [loading, setLoading] = useState(false);

  const save = async () => {
    setLoading(true);
    try {
      const body = { status };
      if (outcome) body.outcome = outcome;
      if (amount)  body.amount  = parseFloat(amount);
      if (notes)   body.notes   = notes;

      const res = await fetch(`${API_BASE}/api/sars/${sub.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (res.ok) { onUpdate(await res.json()); onClose(); }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const sel = "w-full px-3 py-2 rounded-xl border border-luxury-border bg-luxury-elevated text-luxury-text text-sm focus:outline-none focus:ring-2 focus:ring-luxury-gold/30";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-sm rounded-2xl bg-luxury-surface border border-luxury-border shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-luxury-border bg-luxury-elevated">
          <h3 className="font-bold text-luxury-text text-sm">Update Submission</h3>
          <button onClick={onClose} className="p-1 rounded-lg text-luxury-muted hover:text-luxury-text transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div>
            <label className="text-xs font-semibold text-luxury-secondary uppercase tracking-wide mb-1.5 block">Status</label>
            <select value={status} onChange={e => setStatus(e.target.value)} className={sel}>
              <option value="submitted">Submitted</option>
              <option value="processing">Processing</option>
              <option value="assessed">Assessed</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-luxury-secondary uppercase tracking-wide mb-1.5 block">Outcome</label>
            <select value={outcome} onChange={e => setOutcome(e.target.value)} className={sel}>
              <option value="">— None —</option>
              <option value="refund">Refund Due</option>
              <option value="payment">Tax Payable</option>
              <option value="nil">Nil Assessment</option>
            </select>
          </div>
          {outcome && outcome !== "nil" && (
            <div>
              <label className="text-xs font-semibold text-luxury-secondary uppercase tracking-wide mb-1.5 block">Amount (R)</label>
              <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="e.g. 3240" className={sel + " placeholder-luxury-muted"} />
            </div>
          )}
          <div>
            <label className="text-xs font-semibold text-luxury-secondary uppercase tracking-wide mb-1.5 block">Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} className={sel + " resize-none"} />
          </div>
        </div>
        <div className="px-5 py-4 border-t border-luxury-border bg-luxury-elevated flex gap-3">
          <button onClick={save} disabled={loading} className="flex-1 py-2.5 rounded-xl bg-luxury-cta hover:bg-luxury-cta-hover disabled:opacity-50 text-white text-sm font-semibold transition-colors">
            {loading ? "Saving..." : "Save Changes"}
          </button>
          <button onClick={onClose} className="px-4 py-2.5 rounded-xl bg-luxury-bg border border-luxury-border text-luxury-secondary text-sm font-semibold hover:bg-luxury-elevated transition-colors">Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default function SARSSubmissions() {
  const navigate          = useNavigate();
  const { token }         = useAuth();
  const { addToast }      = useToast();
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats]             = useState({});
  const [loading, setLoading]         = useState(true);
  const [showModal, setShowModal]     = useState(false);
  const [editSub, setEditSub]         = useState(null);
  const [expanded, setExpanded]       = useState(null);
  const [filter, setFilter]           = useState("All");

  const fetchSubmissions = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/sars/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setSubmissions(data.submissions || []);
        setStats(data.stats || {});
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchSubmissions(); }, [fetchSubmissions]);

  const handleAdd = (sub) => {
    setSubmissions(p => [sub, ...p]);
    setStats(p => ({ ...p, total: (p.total || 0) + 1 }));
    addToast("Submission logged successfully", "success");
  };

  const handleUpdate = (updated) => {
    setSubmissions(p => p.map(s => s.id === updated.id ? updated : s));
    addToast("Submission updated", "success");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this submission?")) return;
    try {
      const res = await fetch(`${API_BASE}/api/sars/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setSubmissions(p => p.filter(s => s.id !== id));
        addToast("Submission deleted", "success");
      }
    } catch (err) {
      addToast("Failed to delete", "error");
    }
  };

  const filtered = submissions.filter(s => {
    if (filter === "All")       return true;
    if (filter === "Assessed")  return s.status === "assessed";
    if (filter === "Submitted") return s.status === "submitted";
    return s.type === filter;
  });

  return (
    <div className="min-h-screen bg-luxury-bg p-6 lg:p-8">

      {/* ── Header ───────────────────────────────────────────────── */}
      <div className="rounded-2xl bg-luxury-surface border border-luxury-border p-6 lg:p-8 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-8 h-8 rounded-lg bg-luxury-elevated border border-luxury-gold/30 flex items-center justify-center">
                <svg className="w-4 h-4 text-luxury-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <span className="text-xs font-semibold uppercase tracking-wider text-luxury-muted">SARS South Africa</span>
            </div>
            <h1 className="text-2xl font-bold text-luxury-text">SARS Submissions</h1>
            <p className="text-sm text-luxury-secondary mt-1">Track all your tax return submissions and assessment outcomes.</p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button onClick={() => setShowModal(true)}
              className="inline-flex items-center gap-2 bg-luxury-cta hover:bg-luxury-cta-hover text-white font-semibold px-5 py-2.5 rounded-xl transition-colors text-sm active:scale-95">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Log Submission
            </button>
            <button onClick={() => navigate("/upload")}
              className="inline-flex items-center gap-2 bg-luxury-elevated hover:bg-luxury-border border border-luxury-border text-luxury-text font-semibold px-4 py-2.5 rounded-xl transition-colors text-sm">
              <svg className="w-4 h-4 text-luxury-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              Upload Docs
            </button>
          </div>
        </div>
      </div>

      {/* ── Stats ────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { label: "Total Submissions", value: stats.total ?? 0,          color: "text-luxury-gold",                          accent: "border-t-luxury-gold" },
          { label: "Assessed",          value: stats.assessed ?? 0,       color: "text-luxury-cta",                           accent: "border-t-luxury-cta" },
          { label: "Total Refunds",     value: `R${(stats.total_refunds||0).toLocaleString()}`,  color: "text-emerald-600 dark:text-emerald-400", accent: "border-t-emerald-400" },
          { label: "Tax Paid",          value: `R${(stats.total_payments||0).toLocaleString()}`, color: "text-red-600 dark:text-red-400",         accent: "border-t-red-400" },
        ].map(s => (
          <div key={s.label} className={`rounded-2xl p-5 border border-luxury-border border-t-2 bg-luxury-surface transition-all hover:scale-[1.02] ${s.accent}`}>
            <div className={`text-2xl font-bold mb-1 ${s.color}`}>{s.value}</div>
            <div className="text-xs font-medium text-luxury-secondary uppercase tracking-wide">{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── Filters ──────────────────────────────────────────────── */}
      <div className="flex gap-2 flex-wrap mb-5">
        {FILTERS.map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition-all
              ${filter === f
                ? "bg-luxury-cta text-white border-transparent"
                : "bg-luxury-surface border-luxury-border text-luxury-secondary hover:bg-luxury-elevated hover:text-luxury-text"}`}>
            {f}
          </button>
        ))}
      </div>

      {/* ── List ─────────────────────────────────────────────────── */}
      {loading ? (
        <div className="space-y-3">
          {[1,2,3].map(i => <div key={i} className="h-20 rounded-2xl animate-pulse bg-luxury-surface border border-luxury-border" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl bg-luxury-surface border border-luxury-border p-14 text-center">
          <div className="w-14 h-14 rounded-2xl bg-luxury-elevated mx-auto flex items-center justify-center mb-4">
            <svg className="w-7 h-7 text-luxury-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="font-semibold text-luxury-text mb-1">No submissions yet</p>
          <p className="text-sm text-luxury-secondary mb-5">Log your first SARS submission to start tracking.</p>
          <button onClick={() => setShowModal(true)} className="px-5 py-2.5 bg-luxury-cta hover:bg-luxury-cta-hover text-white text-sm font-semibold rounded-xl transition-colors">
            Log First Submission
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(sub => {
            const sc    = STATUS_CONFIG[sub.status] || STATUS_CONFIG.submitted;
            const oc    = sub.outcome ? OUTCOME_CONFIG[sub.outcome] : null;
            const isOpen = expanded === sub.id;
            return (
              <div key={sub.id} className="rounded-2xl bg-luxury-surface border border-luxury-border overflow-hidden hover:border-luxury-gold/20 transition-all">
                <button onClick={() => setExpanded(isOpen ? null : sub.id)} className="w-full px-5 py-4 flex items-center gap-4 text-left">
                  <div className="w-12 h-12 rounded-xl bg-luxury-elevated border border-luxury-gold/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-bold text-luxury-gold">{sub.type}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm text-luxury-text">{sub.type} — Tax Year {sub.tax_year}</span>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${sc.bg} ${sc.text} ${sc.border} flex items-center gap-1`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                        {sc.label}
                      </span>
                    </div>
                    <p className="text-xs text-luxury-muted mt-0.5">
                      Submitted {formatDate(sub.submitted_date)}
                      {sub.ref_number && <span className="ml-2">· Ref: {sub.ref_number}</span>}
                    </p>
                  </div>
                  {oc && (
                    <div className="text-right flex-shrink-0">
                      <div className={`text-sm font-bold ${oc.color}`}>{oc.icon} {sub.amount ? `R${Number(sub.amount).toLocaleString()}` : ""}</div>
                      <div className="text-xs text-luxury-muted">{oc.label}</div>
                    </div>
                  )}
                  <svg className={`w-4 h-4 text-luxury-muted transition-transform flex-shrink-0 ${isOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {isOpen && (
                  <div className="px-5 pb-4 border-t border-luxury-border bg-luxury-elevated">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 py-4">
                      {[
                        { label: "Return Type",     value: sub.type,                    sub: TYPE_INFO[sub.type] },
                        { label: "Tax Year",        value: sub.tax_year,                sub: null },
                        { label: "SARS Reference",  value: sub.ref_number || "—",       sub: null },
                        { label: "Date Submitted",  value: formatDate(sub.submitted_date), sub: null },
                        { label: "Status",          value: null,                        badge: sc },
                        ...(oc ? [{ label: "Outcome", value: `${oc.label}${sub.amount ? ` — R${Number(sub.amount).toLocaleString()}` : ""}`, color: oc.color }] : []),
                      ].map((d, i) => (
                        <div key={i}>
                          <p className="text-xs font-semibold text-luxury-muted uppercase tracking-wide mb-1">{d.label}</p>
                          {d.badge ? (
                            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${d.badge.bg} ${d.badge.text} ${d.badge.border}`}>{d.badge.label}</span>
                          ) : (
                            <p className={`text-sm font-medium ${d.color || "text-luxury-text"}`}>{d.value}</p>
                          )}
                          {d.sub && <p className="text-xs text-luxury-muted mt-0.5">{d.sub}</p>}
                        </div>
                      ))}
                    </div>
                    {sub.notes && (
                      <div className="pt-3 border-t border-luxury-border">
                        <p className="text-xs font-semibold text-luxury-muted uppercase tracking-wide mb-1">Notes</p>
                        <p className="text-sm text-luxury-secondary">{sub.notes}</p>
                      </div>
                    )}
                    <div className="flex gap-2 mt-4">
                      <button onClick={() => setEditSub(sub)} className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-luxury-surface border border-luxury-border text-luxury-secondary hover:text-luxury-gold hover:border-luxury-gold/30 transition-colors">
                        Update Status
                      </button>
                      <button onClick={() => navigate("/documents")} className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-luxury-surface border border-luxury-border text-luxury-secondary hover:text-luxury-gold hover:border-luxury-gold/30 transition-colors">
                        View Documents
                      </button>
                      <button onClick={() => window.open("https://efiling.sars.gov.za", "_blank")} className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-luxury-surface border border-luxury-border text-luxury-secondary hover:text-luxury-gold hover:border-luxury-gold/30 transition-colors">
                        SARS eFiling ↗
                      </button>
                      <button onClick={() => handleDelete(sub.id)} className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-luxury-surface border border-red-200 dark:border-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors ml-auto">
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Footer note */}
      <div className="mt-6 rounded-xl bg-luxury-surface border border-luxury-border px-5 py-4 flex items-start gap-3">
        <svg className="w-4 h-4 text-luxury-muted flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-xs text-luxury-muted leading-relaxed">
          All submissions are saved to your TaxVault account. For official status always verify on{" "}
          <span className="text-luxury-gold font-medium cursor-pointer" onClick={() => window.open("https://efiling.sars.gov.za","_blank")}>efiling.sars.gov.za</span>.
        </p>
      </div>

      {showModal && <NewSubmissionModal onClose={() => setShowModal(false)} onAdd={handleAdd} token={token} />}
      {editSub   && <UpdateModal sub={editSub} onClose={() => setEditSub(null)} onUpdate={handleUpdate} token={token} />}
    </div>
  );
}
